import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { attorneyAPI, paymentAPI, getErrorKey } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const SuccessPopup = ({ onClose }) => (
  <div style={{
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  }}>
    <div style={{
      backgroundColor: 'white', borderRadius: '16px', padding: '2.5rem 2rem',
      maxWidth: '420px', width: '90%', textAlign: 'center',
      boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    }}>
      <div style={{ marginBottom: '1rem' }}>
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
        </svg>
      </div>
      <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1a365d', marginBottom: '0.75rem' }}>
        Обуна фаоллаштирилди!
      </h2>
      <p style={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: '1.65', marginBottom: '1.5rem' }}>
        Тўловингиз қабул қилинди. Бир ойлик обунангиз фаол.
      </p>
      <button onClick={onClose} style={{
        backgroundColor: '#1a365d', color: 'white', border: 'none',
        padding: '0.8rem 2rem', borderRadius: '10px', fontSize: '0.95rem',
        fontWeight: '600', cursor: 'pointer',
      }}>
        Тушунарли
      </button>
    </div>
  </div>
);

const TIER_RANK = { free: 0, pro: 1, vip: 2 };

const PLANS = [
  { key: 'free', name: 'Free', price: null, tagline: 'Платформа билан танишиш учун' },
  { key: 'pro', name: 'Pro', price: 99000, tagline: 'Профессионал профилингизни ривожлантиринг' },
  { key: 'vip', name: 'VIP', price: 249000, tagline: 'Кўпроқ мижозлар ва максимал кўринувчанлик учун' },
];

// One row per feature — matches what's actually gated server-side (utils/tier.js's
// requireTier/requireAttorneyTier checks), not an aspirational list.
const FEATURES = [
  { label: 'Асосий маълумотларни эълон қилиш', free: true, pro: true, vip: true },
  { label: 'Профил қидирувда кўринади', free: true, pro: true, vip: true },
  { label: 'Мижозлар сўровлари рўйхатини кўриш', free: true, pro: true, vip: true },
  { label: 'Мижозлар сўровларида телефон рақамини тўлиқ кўриш', free: false, pro: true, vip: true },
  { label: 'Тўлиқ профил маълумоти (ижтимоий тармоқлар)', free: false, pro: true, vip: true },
  { label: 'Мижозлар сақлаб қўйиши мумкин', free: false, pro: true, vip: true },
  { label: 'Мижозлардан тўғридан-тўғри сўров олиш', free: false, pro: false, vip: true },
  { label: 'Мақола чоп этиш', free: false, pro: false, vip: true },
  { label: 'Мижозлар баҳо қўйиши мумкин', free: false, pro: false, vip: true },
  { label: 'AdvokatTop дан шахсий рейтинг олиш', free: false, pro: false, vip: true },
  { label: "Google'да кўринувчанлик (индексланиш)", free: false, pro: false, vip: true },
  { label: 'Профил ва мақола кўришлар статистикаси', free: false, pro: false, vip: true },
];

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const Dash = () => <span style={s.dash}>—</span>;

const PayButton = ({ src, alt, accent, accentRgb, onClick, disabled, loading }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...s.payBtn,
        borderColor: hover && !disabled ? accent : '#e2e8f0',
        boxShadow: hover && !disabled ? `0 2px 12px ${accentRgb}` : 'none',
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {loading ? <span style={s.payBtnLoading}>...</span> : <img src={src} alt={alt} style={{ height: '20px', objectFit: 'contain' }} />}
    </button>
  );
};

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null); // { plan, provider } | null
  const [error, setError] = useState('');
  const [showPaidPopup, setShowPaidPopup] = useState(false);

  useEffect(() => {
    // Payme/Click redirect back to this URL with paid=1 whether the user actually completed
    // payment or just cancelled/backed out of checkout — it's not proof of payment, only a hint
    // to check. Confirm against the real subscription status before showing the success popup.
    const params = new URLSearchParams(location.search);
    const cameFromCheckout = params.get('paid') === '1';
    if (cameFromCheckout) window.history.replaceState({}, '', '/subscription');

    attorneyAPI.getSubscriptionStatus()
      .then(res => {
        setSubscription(res.data);
        if (cameFromCheckout && res.data?.isSubscribed) setShowPaidPopup(true);
      })
      .catch(() => setSubscription(null))
      .finally(() => setLoading(false));
  }, [location.search]);

  const handleSubscribe = async (planType, provider) => {
    setProcessing({ plan: planType, provider });
    setError('');
    try {
      const fn = provider === 'payme'
        ? paymentAPI.initiatePaymeSubscription
        : paymentAPI.initiateClickSubscription;
      const res = await fn(planType);
      window.location.href = res.data.payUrl;
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'subscriptionFailed'}`));
      setProcessing(null);
    }
  };

  const currentTier = subscription?.tier || 'free';
  const isActive = subscription?.isSubscribed;
  const expiresAt = subscription?.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString('uz-UZ') : null;

  if (loading) return <div style={s.center}>Юкланмоқда...</div>;

  return (
    <div style={s.page}>
      {showPaidPopup && <SuccessPopup onClose={() => { setShowPaidPopup(false); navigate('/attorney/dashboard'); }} />}

      <div style={s.container}>
        <div style={s.header}>
          <h1 style={s.title}>Обуна</h1>
          <p style={s.subtitle}>Платформа имкониятларидан тўлиқ фойдаланиш учун режани танланг</p>
        </div>

        {isActive && (
          <div style={s.activeBanner}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#276749" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
            </svg>
            <div>
              <div style={{ fontWeight: '700', color: '#276749', marginBottom: '0.15rem' }}>
                {PLANS.find(p => p.key === currentTier)?.name} режаси фаол
              </div>
              {expiresAt && <div style={{ fontSize: '0.9rem', color: '#276749' }}>Амал қилиш муддати: {expiresAt}</div>}
            </div>
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.cornerCell} />
                {PLANS.map(plan => {
                  const isCurrent = plan.key === currentTier;
                  const isUpgrade = TIER_RANK[plan.key] > TIER_RANK[currentTier];
                  return (
                    <th key={plan.key} style={{ ...s.headerCell, ...(isCurrent ? s.headerCellActive : {}) }}>
                      <div style={s.planName}>{plan.name}</div>
                      <div style={s.tagline}>{plan.tagline}</div>
                      {plan.price ? (
                        <div style={s.price}>{plan.price.toLocaleString()} <span style={s.currency}>сўм</span><div style={s.period}>ойига</div></div>
                      ) : (
                        <div style={s.price}>0 <span style={s.currency}>сўм</span><div style={s.period}>ҳамиша бепул</div></div>
                      )}

                      <div style={s.headerAction}>
                        {isCurrent ? (
                          <div style={s.currentBadge}>✓ Жорий режа</div>
                        ) : plan.key === 'free' ? (
                          <div style={s.na}>—</div>
                        ) : isUpgrade ? (
                          <div style={s.payRow}>
                            <PayButton
                              src="/payme_logo.svg" alt="Payme" accent="#0cbbbc" accentRgb="rgba(12,187,188,0.18)"
                              onClick={() => handleSubscribe(plan.key, 'payme')}
                              disabled={!!processing}
                              loading={processing?.plan === plan.key && processing?.provider === 'payme'}
                            />
                            <PayButton
                              src="/click_logo.svg" alt="Click" accent="#0065FF" accentRgb="rgba(0,101,255,0.15)"
                              onClick={() => handleSubscribe(plan.key, 'click')}
                              disabled={!!processing}
                              loading={processing?.plan === plan.key && processing?.provider === 'click'}
                            />
                          </div>
                        ) : (
                          <div style={s.na}>—</div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((f, i) => (
                <tr key={i} style={i % 2 === 1 ? s.rowAlt : undefined}>
                  <td style={s.featureCell}>{f.label}</td>
                  {PLANS.map(plan => {
                    const isCurrent = plan.key === currentTier;
                    return (
                      <td key={plan.key} style={{ ...s.valueCell, ...(isCurrent ? s.valueCellActive : {}) }}>
                        {f[plan.key] ? <Check /> : <Dash />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { backgroundColor: '#f7fafc', minHeight: 'calc(100vh - 120px)', padding: '3rem 2rem' },
  container: { maxWidth: '1080px', margin: '0 auto' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#718096' },
  header: { textAlign: 'center', marginBottom: '2rem' },
  title: { fontSize: '2rem', fontWeight: '800', color: '#1a365d', marginBottom: '0.4rem' },
  subtitle: { color: '#718096', fontSize: '1rem' },
  activeBanner: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    backgroundColor: '#f0fff4', border: '1px solid #38a169',
    borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem',
  },
  error: {
    backgroundColor: '#fed7d7', color: '#c53030', padding: '0.75rem 1rem',
    borderRadius: '8px', fontSize: '0.9rem', marginBottom: '1rem',
  },

  tableWrap: {
    backgroundColor: 'white', borderRadius: '18px', boxShadow: '0 4px 24px rgba(20,30,60,0.08)',
    border: '1px solid #e8edf3', overflowX: 'auto',
  },
  table: { width: '100%', minWidth: '820px', borderCollapse: 'collapse' },

  cornerCell: { width: '32%', backgroundColor: '#f7fafc', borderBottom: '2px solid #e8edf3' },
  headerCell: {
    verticalAlign: 'top', textAlign: 'center', padding: '1.5rem 1.1rem 1.25rem',
    backgroundColor: '#f7fafc', borderBottom: '2px solid #e8edf3', borderLeft: '1px solid #eef2f6',
    width: '22.7%',
  },
  headerCellActive: { backgroundColor: '#ebf5ff' },
  planName: { fontSize: '1.05rem', fontWeight: '800', color: '#1a365d', marginBottom: '0.35rem', letterSpacing: '0.01em' },
  tagline: { fontSize: '0.76rem', fontWeight: '500', color: '#7c8aa0', lineHeight: 1.35, marginBottom: '0.9rem', minHeight: '2.4em' },
  price: { fontSize: '1.5rem', fontWeight: '800', color: '#1a365d', lineHeight: 1.1 },
  currency: { fontSize: '0.9rem', fontWeight: '600', color: '#4a5568' },
  period: { fontSize: '0.72rem', fontWeight: '500', color: '#a0aec0', marginTop: '0.3rem' },
  headerAction: { marginTop: '1rem', display: 'flex', justifyContent: 'center', minHeight: '38px', alignItems: 'center' },
  currentBadge: {
    display: 'inline-block', backgroundColor: '#c6f6d5', color: '#276749',
    padding: '0.35rem 0.8rem', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '700',
  },
  na: { color: '#cbd5e0', fontSize: '1rem' },
  payRow: { display: 'flex', gap: '0.5rem', justifyContent: 'center' },
  payBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0.5rem 0.7rem', borderRadius: '10px', border: '1.5px solid #e2e8f0',
    backgroundColor: 'white', transition: 'border-color 0.15s, box-shadow 0.15s',
    minWidth: '52px', height: '38px',
  },
  payBtnLoading: { fontSize: '0.85rem', color: '#a0aec0', fontWeight: '700' },

  featureCell: {
    padding: '0.85rem 1.25rem', fontSize: '0.9rem', color: '#3c4759', fontWeight: '500',
    borderBottom: '1px solid #eef2f6', textAlign: 'left',
  },
  rowAlt: { backgroundColor: '#fafbfc' },
  valueCell: {
    padding: '0.85rem 0.5rem', textAlign: 'center', borderBottom: '1px solid #eef2f6', borderLeft: '1px solid #eef2f6',
  },
  valueCellActive: { backgroundColor: '#f4f9ff' },
  dash: { color: '#d3dae3', fontSize: '1rem', fontWeight: '700' },
};

export default Subscription;
